<script setup lang="ts">
import CategoryBrandNav from "@/components/common/product/CategoryBrandNav.vue";
import ProductHeaderPromIcons from "@/components/common/product/ProductHeaderPromIcons.vue";
import ProductAltDescription from "@/components/product/ProductAltDescription.vue";
import ProductGallery from "@/components/product/ProductGallery.vue";
import ProductPlaceholder from "@/components/product/ProductPlaceholder.vue";
import ProductPricing from "@/components/product/ProductPricing.vue";
import ProductPromotionList from "@/components/product/ProductPromotionList.vue";
import ProductStickyNav from "@/components/product/ProductStickyNav.vue";
import ProductStockSelector from "@/components/product/ProductStockSelector.vue";
import ProductTitleWithRating from "@/components/product/ProductTitleWithRating.vue";
import { ProductStickyNavConfig } from "~/config/product";
import type Product from "~/models/Product";
import googleLdJsonService from "~/services/GoogleLdJsonService";
import productService from "~/services/ProductService";
import seoService from "~/services/SeoService";
import useProductStore from "@/stores/product";
import BannerByBrandAndCategory from "@/components/common/product/BannerByBrandAndCategory.vue";
import {
  breakpointsBootstrapV5,
  useBreakpoints,
  useIntersectionObserver,
  useMounted,
} from "@vueuse/core";

const props = defineProps<{
  slug: string;
  partNumber?: string | null;
}>();

const route = useRoute();
const router = useRouter();
const nuxtApp = useNuxtApp();
const ssrWidth = useResponsiveSsrWidth();
const breakpoints = useBreakpoints(breakpointsBootstrapV5, { ssrWidth });
const productStore = useProductStore();

const isMobileSize = breakpoints.smallerOrEqual("md");

const matchesCurrentRoute = (currentProduct?: Product) => {
  if (!currentProduct) {
    return false;
  }

  if ((currentProduct.slug || "") !== props.slug) {
    return false;
  }

  if (!props.partNumber) {
    return true;
  }

  return currentProduct.seo_part_number === props.partNumber;
};

const hydratedStoreProduct = productStore.$getProduct();
const product = ref<Product | undefined>(
  matchesCurrentRoute(hydratedStoreProduct) ? hydratedStoreProduct : undefined,
);
const loading = ref(false);
const redirecting = ref(false);
const hasError = ref(false);

const isNavSticky = ref(false);
const handleIsSticky = (isSticky: boolean) => {
  isNavSticky.value = isSticky;
};

const appendProductScript = (
  productModel: Product,
  extra: { productLink: string },
) => {
  const categoryTrail = [];
  let category = productModel.category;
  while (category) {
    categoryTrail.push(category);
    category = category.parent || null;
  }
  categoryTrail.reverse();

  const breadcrumbItems = [
    {
      name: "Inicio",
      item: new URL("/", extra.productLink).toString(),
    },
    {
      name: "Categorías",
      item: new URL(
        router.resolve({ name: "search_categories" }).fullPath,
        extra.productLink,
      ).toString(),
    },
    ...categoryTrail.map((currentCategory) => ({
      name: currentCategory.name || "Categoría",
      item: new URL(
        currentCategory.resolveSearchRoute(router)?.fullPath || "/",
        extra.productLink,
      ).toString(),
    })),
    {
      name: productModel.name || "Producto",
      item: extra.productLink,
    },
  ];

  const scriptAttrs = [
    {
      key: "product-structured-data",
      ...googleLdJsonService.formatScript(
        googleLdJsonService.formatProduct(productModel, extra),
      ),
    },
    {
      key: "product-breadcrumb-structured-data",
      ...googleLdJsonService.formatScript(
        googleLdJsonService.formatBreadcrumbList(breadcrumbItems),
      ),
    },
  ];
  if (import.meta.env.SSR) {
    nuxtApp.runWithContext(() => {
      useHead({
        script: scriptAttrs,
      });
    });
  } else {
    document
      .querySelectorAll("script[data-digishop-structured-data='product']")
      .forEach((script) => script.remove());
    scriptAttrs.forEach((scriptAttrsItem) => {
      const script = document.createElement("script");
      Object.assign(script, scriptAttrsItem);
      script.dataset.digishopStructuredData = "product";
      document.head.append(script);
    });
  }
};

const setProductSeo = () => {
  if (!product.value) {
    return;
  }

  const productLink = getAbsoluteProductLink();
  if (!productLink) {
    return;
  }

  seoService.setProduct(product.value, productLink);
};

const getAbsoluteProductLink = () => {
  if (!product.value) {
    return null;
  }

  const relativePath = product.value.resolveLink(router).fullPath;

  try {
    return new URL(
      relativePath,
      "https://" + import.meta.env.VITE_APP_DOMAIN,
    ).toString();
  } catch {
    return null;
  }
};

const setGoogleLdJson = () => {
  if (!product.value) {
    return;
  }

  const productLink = getAbsoluteProductLink();
  if (!productLink) {
    return;
  }

  appendProductScript(product.value, { productLink });
};

const loadReviewsAndSetGoogleLdJson = async () => {
  if (!product.value) {
    return;
  }

  await product.value.fetchMoreReviews();
  setGoogleLdJson();
  productStore.$setProduct(product.value);
};

const syncCanonicalRoute = async () => {
  if (!product.value) {
    return false;
  }

  const canonical = product.value.resolveLink(router);
  if (canonical.fullPath === route.fullPath) {
    redirecting.value = false;
    return false;
  }

  redirecting.value = true;
  await nuxtApp.runWithContext(() =>
    navigateTo(canonical.fullPath, {
      replace: true,
      redirectCode: 301,
    }),
  );

  return true;
};

const hydrateProductView = async () => {
  if (!product.value) {
    return false;
  }

  if (await syncCanonicalRoute()) {
    return true;
  }

  await loadReviewsAndSetGoogleLdJson();
  nuxtApp.runWithContext(() => {
    setProductSeo();
  });

  return false;
};

const loadProduct = async () => {
  if (loading.value) {
    return;
  }

  loading.value = true;
  redirecting.value = false;
  hasError.value = false;
  try {
    product.value = props.partNumber
      ? await productService.findByRoute(props.partNumber, props.slug)
      : await productService.findBySlug(props.slug);
  } catch (err) {
    console.error(err);
    product.value = undefined;
    productStore.$clearProduct();
    hasError.value = true;
  } finally {
    loading.value = false;
  }
};

const loadCurrentRouteProduct = async () => {
  if (!props.slug) {
    product.value = undefined;
    productStore.$clearProduct();
    hasError.value = true;
    loading.value = false;
    return;
  }

  if (matchesCurrentRoute(product.value)) {
    return;
  }

  product.value = undefined;
  await loadProduct();
  await hydrateProductView();
};

watch(
  () => [props.partNumber ?? null, props.slug],
  async () => {
    if (import.meta.client) {
      await loadCurrentRouteProduct();
    }
  },
);

if (import.meta.server && !product.value && props.slug) {
  await loadCurrentRouteProduct();
}

if (import.meta.client && !product.value && props.slug) {
  if (nuxtApp.isHydrating && nuxtApp.payload.serverRendered) {
    await loadCurrentRouteProduct();
  } else {
    void loadCurrentRouteProduct();
  }
}

onUnmounted(() => {
  productStore.$clearProduct();
});

const relatedSectionCt = ref<HTMLElement>();
const showRelatedSection = ref(false);
const desktopContainer = ref<HTMLElement>();
const mounted = useMounted();

useIntersectionObserver(relatedSectionCt, ([observer]) => {
  if (observer?.isIntersecting && !showRelatedSection.value) {
    showRelatedSection.value = true;
  }
});
</script>

<template>
  <div
    v-if="product && !redirecting"
    :id="product.componentId"
    class="p-2 p-md-0"
  >
    <category-brand-nav v-if="product.category" :category="product.category" />
    <section class="mb-3">
      <div class="row">
        <div class="col-12">
          <product-title-with-rating
            v-if="isMobileSize && mounted"
            :product="product"
          />
        </div>
        <div class="col-md product-main-column">
          <product-gallery :product="product" />
        </div>
        <div class="col-12 d-md-none">
          <product-sticky-nav
            v-if="desktopContainer && mounted"
            :product="product"
            class="mb-2 mb-md-4"
            @is-sticky="handleIsSticky"
          />
        </div>
        <div
          class="col-md-5 mb-3 mb-md-0 d-flex flex-column justify-content-between product-main-column"
        >
          <product-title-with-rating
            v-if="!isMobileSize || !mounted"
            :product="product"
          />
          <div class="d-flex align-items-center mb-3 mb-md-4 gap-1 flex-wrap">
            <product-header-prom-icons
              class="mt-2 mt-md-0"
              :product="product"
            />
          </div>
          <product-pricing :product="product" />
          <product-stock-selector :product="product" />
        </div>
        <div class="col-md-2">
          <product-promotion-list :product="product" />
        </div>
      </div>
      <banner-by-brand-and-category :product="product" />
      <div
        class="border-bottom border-light-subtle shadow-bottom"
        style="height: 5px"
      ></div>
    </section>
    <div class="row">
      <div class="col-md-8">
        <div
          ref="desktopContainer"
          :class="[ProductStickyNavConfig.DesktopContainerCssClass]"
          class="mb-3"
        ></div>
        <div ref="relatedSectionCt">
          <template v-if="showRelatedSection">
            <LazyProductRelated :product="product" :relation="'components'" />
            <LazyProductRelated :product="product" :relation="'sets'" />
            <LazyProductRelated :product="product" :relation="'substitutes'" />
            <LazyProductRelated :product="product" :relation="'related'" />
          </template>
        </div>
        <LazyProductDescription :product="product" />
      </div>
      <div class="col-md-4">
        <product-alt-description :product="product" />
      </div>
    </div>
  </div>
  <div v-else-if="loading || redirecting" class="p-2 p-md-0">
    <product-placeholder />
    <div
      v-if="redirecting"
      class="text-center text-secondary small py-3"
      role="status"
    >
      Redirigiendo al producto...
    </div>
  </div>
  <div v-else-if="hasError" class="alert alert-danger text-center" role="alert">
    No se pudo obtener el recurso solicitado, recarga la página o intenta más
    tarde.
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/scss/variables" as *;
@use "@/assets/scss/bootstrap-helpers" as *;

.shadow-bottom {
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.075);
}

.product-main-column {
  min-width: 0;
}

.is-sticky {
  position: sticky;
  top: $mobile-nav-height;
  z-index: 1020;
  overflow: hidden;
  .nav-product {
    display: none;
  }
  &.is-sticky {
    .nav-product {
      display: block;
    }
  }
}

@include media-breakpoint-up(md) {
  .is-sticky {
    top: 0;
  }
}
</style>
